/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { AnyAction, createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';

import { ApiStatus } from '../../../../ts';
import { SkillReferenceType, SkillType } from '../../../../ts/skill';
import { EnvironmentsSlice } from '../../../../ts/state';
import { formatAssistantToEnvironment } from '../../api/environments';
import { updateAssistant } from '../assistants/actions';
import { switchVersion } from '../publish/actions';
import { deployRelease } from '../releases/actions';
import { SET_ASSISTANT_ID, SET_TENANT } from '../session/actionTypes';
import { createSkill } from '../skills/actions';
import {
  createEnvironment,
  deleteEnvironment,
  getEnvironment,
  listEnvironmentsWithArgs,
  updateEnvironment,
  resetAllEnvironments
} from './actions';
import { entityAdapter } from './adapter';

// Initial State
const initialState = entityAdapter.getInitialState({
  getStatus: ApiStatus.IDLE,
  getError: null,

  listStatus: ApiStatus.IDLE, // idle, pending, fulfilled, error
  listFetched: null,
  listError: null,

  createStatus: ApiStatus.IDLE,
  createError: null,

  updateStatus: ApiStatus.IDLE,
  updateError: null,

  deleteStatus: ApiStatus.IDLE,
  deleteError: null
});

/**
 * Gets the initial state of the redux slice and apply updates.
 * @param {*} updates The updates to apply to the initial state
 * @returns {*} The slice initial state
 */
export const computeState = (updates?: any): EnvironmentsSlice => ({
  ..._.cloneDeep(initialState),
  ...updates
});

// Slice + Reducer
const slice = createSlice({
  name: 'environments',
  initialState: computeState(),
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(resetAllEnvironments, (state, _) => {
        // Don't reset if another invocation is in the middle of updating
        if (state.listStatus === ApiStatus.LOADING) {
          return;
        }
        //Reset state
        computeState();
      })
      .addCase(getEnvironment.pending, (state, _) => {
        state.getStatus = ApiStatus.LOADING;
        state.getError = null;
      })
      .addCase(getEnvironment.fulfilled, (state, action) => {
        state.getStatus = ApiStatus.SUCCEEDED;
        state.getError = null;
        entityAdapter.upsertOne(state, action.payload);
      })
      .addCase(getEnvironment.rejected, (state, action) => {
        state.getStatus = ApiStatus.ERROR;
        state.getError = action.error;
      })
      .addCase(listEnvironmentsWithArgs.pending, (state, _action) => {
        state.listStatus = ApiStatus.LOADING;
        state.listError = null;
      })
      .addCase(listEnvironmentsWithArgs.fulfilled, (state, action) => {
        state.listStatus = ApiStatus.SUCCEEDED;
        state.listFetched = Date.now();
        state.listError = null;
        entityAdapter.setAll(state, action.payload);
      })
      .addCase(listEnvironmentsWithArgs.rejected, (state, action) => {
        state.listStatus = ApiStatus.ERROR;
        state.listError = action.error;
      })
      .addCase(createEnvironment.pending, (state, _action) => {
        state.createStatus = ApiStatus.LOADING;
        state.createError = null;
      })
      .addCase(createEnvironment.fulfilled, (state, action) => {
        state.createStatus = ApiStatus.SUCCEEDED;
        state.createError = null;
        entityAdapter.addOne(state, action.payload);
      })
      .addCase(createEnvironment.rejected, (state, action) => {
        state.createStatus = ApiStatus.ERROR;
        state.createError = action.error;
      })
      .addCase(updateEnvironment.pending, (state, _action) => {
        state.updateStatus = ApiStatus.LOADING;
        state.updateError = null;
      })
      .addCase(updateEnvironment.fulfilled, (state, action) => {
        state.updateStatus = ApiStatus.SUCCEEDED;
        state.updateError = null;
        entityAdapter.upsertOne(state, action.payload);
      })
      .addCase(updateEnvironment.rejected, (state, action) => {
        state.updateStatus = ApiStatus.ERROR;
        state.updateError = action.error;
      })
      .addCase(deleteEnvironment.pending, (state, _action) => {
        state.deleteStatus = ApiStatus.LOADING;
        state.deleteError = null;
      })
      .addCase(deleteEnvironment.fulfilled, (state, action) => {
        state.deleteStatus = ApiStatus.SUCCEEDED;
        state.deleteError = null;
        state.listFetched = null;
        entityAdapter.removeOne(state, action.payload.environmentId);
      })
      .addCase(updateAssistant.fulfilled, (state, action) => {
        const environment = formatAssistantToEnvironment(action.payload);
        // FIXME: remove this once store supports multi environments.
        if (state.entities[environment.environment_id]) {
          entityAdapter.upsertOne(
            state,
            _.merge(state.entities[environment.environment_id], environment)
          );
        }
      })
      .addCase(deleteEnvironment.rejected, (state, action) => {
        state.deleteStatus = ApiStatus.ERROR;
        state.deleteError = action.error;
      })
      .addCase(deployRelease.fulfilled, (state, action) => {
        // After a release we update the environment only if it's already loaded.
        const environmentId = action.payload.environment_id;
        if (state.entities[environmentId]) {
          entityAdapter.upsertOne(state, action.payload);
        }
      })
      .addCase(createSkill.fulfilled, (state, action) => {
        const skill = action.payload;
        if (skill.type === SkillType.SEARCH) {
          const env = state.entities[skill.assistant_id];
          if (env) {
            env.skill_references.push({
              type: SkillType.SEARCH,
              skill_reference: SkillReferenceType.SEARCH,
              skill_id: skill.skill_id,
              created: skill.created,
              updated: skill.updated
            });
          }
          state.listFetched = null;
        }
      })
      .addCase(switchVersion.fulfilled, (state, action) => {
        // After a switch version we update the environment only if it's already loaded.
        const environmentId = action.payload.environment_id;
        if (state.entities[environmentId]) {
          entityAdapter.upsertOne(state, action.payload);
        }
      })

      .addCase<string, AnyAction>(SET_TENANT, (state, action) => {
        // Clear the cache if the tenant changes
        if (action.instance) {
          state.listFetched = null;
        }
      })
      .addCase<string, AnyAction>(SET_ASSISTANT_ID, (state, action) => {
        // Clear the cache if the assistant changes
        if (action.assistantId) {
          return computeState();
        }
      });
  }
});

export default slice.reducer;
